import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { Printer, X, Plus, ShieldCheck, Link2 } from "lucide-react";
import { TID } from "@/constants/testIds";

const TYPE_LABEL = {
  ticket: "Factura simplificada",
  factura: "Factura completa",
  abono: "Factura rectificativa (abono)",
};

export default function TicketPreview({ sale, onClose, onNew }) {
  if (!sale) return null;
  const v = sale.verifactu || {};
  const isAbono = sale.doc_type === "abono";

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div data-testid={TID.ticketModal} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col md:flex-row">
        {/* Left: ticket paper */}
        <div className="flex-1 bg-slate-100 p-6 flex justify-center overflow-y-auto thin-scroll">
          <div className="ticket-paper">
            <div className="center">
              <div className="font-semibold text-sm">TPV RETAIL DEMO SL</div>
              <div className="text-[10px]">CIF: B12345678</div>
              <div className="text-[10px]">C/ Mayor 1 · 28013 Madrid</div>
              <div className="text-[10px] mt-1">{TYPE_LABEL[sale.doc_type]}</div>
            </div>
            <div className="dash" />
            <div className="flex justify-between text-[11px]">
              <div>Nº: <b>{sale.serie}/{sale.numero}</b></div>
              <div>{new Date(sale.closed_at || sale.created_at).toLocaleString("es-ES")}</div>
            </div>
            <div className="text-[11px]">Caja: {sale.till_id} · {sale.user_name}</div>
            {sale.customer_name && (
              <div className="text-[11px]">Cliente: {sale.customer_name}{sale.customer_nif ? ` · ${sale.customer_nif}` : ""}</div>
            )}
            <div className="dash" />
            <table className="w-full text-[11px]">
              <thead>
                <tr className="border-b border-dashed border-slate-300">
                  <th className="text-left">Descripción</th>
                  <th className="text-right">Cant.</th>
                  <th className="text-right">PVP</th>
                  <th className="text-right">Importe</th>
                </tr>
              </thead>
              <tbody>
                {sale.lines.map((l) => (
                  <tr key={l.line_id} className="align-top">
                    <td className="py-1">{l.name}</td>
                    <td className="text-right">{l.qty}</td>
                    <td className="text-right">{l.unit_price.toFixed(2)}</td>
                    <td className="text-right">{l.subtotal.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="dash" />
            <div className="text-[11px] space-y-0.5">
              <div className="flex justify-between"><span>Base imponible</span><span>{sale.subtotal.toFixed(2)}€</span></div>
              <div className="flex justify-between"><span>Total IVA</span><span>{sale.tax_total.toFixed(2)}€</span></div>
              <div className="flex justify-between text-base font-semibold border-t border-slate-300 pt-1 mt-1">
                <span>TOTAL</span><span>{sale.total.toFixed(2)}€</span>
              </div>
            </div>
            <div className="dash" />
            <div className="text-[11px] space-y-0.5">
              {sale.payments?.map((p, i) => (
                <div key={i} className="flex justify-between">
                  <span className="capitalize">{p.method}</span>
                  <span>{p.amount.toFixed(2)}€</span>
                </div>
              ))}
              {sale.change_given > 0 && (
                <div className="flex justify-between text-emerald-700">
                  <span>Cambio</span><span>{sale.change_given.toFixed(2)}€</span>
                </div>
              )}
            </div>
            <div className="dash" />
            <div className="center">
              <div className="my-2 flex justify-center">
                {v.qr_url && (
                  <QRCodeSVG value={v.qr_url} size={120} level="M" includeMargin={false} />
                )}
              </div>
              <div className="font-semibold text-[12px] tracking-wider">{v.mencion || "VERI*FACTU"}</div>
              <div className="text-[9px] mt-0.5">Factura verificable en la sede electrónica de la AEAT</div>
              <div className="text-[9px] text-slate-500 mt-1">
                Huella: {v.huella_actual?.slice(0, 16)}…{v.huella_actual?.slice(-8)}
              </div>
            </div>
            <div className="dash" />
            <div className="center text-[10px]">¡Gracias por su visita!</div>
          </div>
        </div>

        {/* Right: actions + metadata */}
        <div className="md:w-[300px] bg-white border-l border-slate-200 p-5 flex flex-col">
          <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">{isAbono ? "Abono emitido" : "Cobro completado"}</div>
          <div className="font-display text-2xl font-semibold mt-1 text-slate-900">
            {sale.total.toFixed(2)}€
          </div>
          <div className="text-sm text-slate-500">{sale.serie}/{sale.numero}</div>

          <div className="mt-4 space-y-2 text-xs">
            <div className="p-3 rounded-md border border-emerald-200 bg-emerald-50 flex items-start gap-2">
              <ShieldCheck size={16} className="text-emerald-700 shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-emerald-900">Verifactu OK</div>
                <div className="text-emerald-800 break-all font-mono text-[10px] mt-1">
                  <Link2 size={10} className="inline" /> {v.huella_actual?.slice(0, 24)}…
                </div>
                <div className="text-emerald-700 text-[10px] mt-1">Tipo {v.tipo_factura} · {v.timestamp_registro?.slice(0,19).replace("T"," ")}</div>
              </div>
            </div>
            <div className={`p-3 rounded-md border flex items-start gap-2 ${
              sale.sage_status === "enviado" ? "border-blue-200 bg-blue-50" : "border-amber-200 bg-amber-50"
            }`}>
              <div className={`status-dot ${sale.sage_status === "enviado" ? "online" : "offline"} mt-1`} />
              <div>
                <div className="font-medium text-slate-900">
                  {sale.sage_status === "enviado" ? "Sage 200c · Enviado" : "Sage Offline · En cola"}
                </div>
                {sale.sage_doc_ref && (
                  <div className="font-mono text-[10px] text-slate-600 mt-1">{sale.sage_doc_ref}</div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-auto pt-4 space-y-2">
            <button
              onClick={() => window.print()}
              className="w-full h-12 rounded-md border border-slate-200 hover:bg-slate-50 text-sm font-medium flex items-center justify-center gap-2"
            >
              <Printer size={16} /> Imprimir
            </button>
            <button
              data-testid={TID.ticketNew}
              onClick={onNew}
              className="w-full h-12 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 flex items-center justify-center gap-2"
            >
              <Plus size={16} /> Nuevo ticket
            </button>
            <button
              data-testid={TID.ticketClose}
              onClick={onClose}
              className="w-full h-10 rounded-md text-slate-500 hover:bg-slate-50 text-sm flex items-center justify-center gap-1"
            >
              <X size={14} /> Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
